import React, { useState, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import makeAnimated from 'react-select/animated';
import colors from 'styles/colors';

import useLocationStore from 'stores/LocationStore';
import { ReactComponent as CloseIcon } from 'assets/images/icons/close/close.svg';

const animatedComponents = makeAnimated();

const styles = {
  multiValueLabel: (provided, _state) => ({
    ...provided,
    border: 'none',
    whiteSpace: 'normal',
    '@media (max-width: 768px)': {
      fontSize: '14px',
    },
    '@media (max-width: 380px)': {
      fontSize: '13px',
    },
  }),
  control: (provided, state) => ({
    ...provided,
    height: '100%',
    border: 'none !important',
    boxShadow: 'none',
    outline: 'none',
    fontSize: '16px',
    '@media (max-width: 768px)': {
      fontSize: '14px',
    },
    '@media (max-width: 380px)': {
      fontSize: '13px',
    },

    '&:hover': {
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },
    '&:focus': {
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },
    '&:active': {
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },
    '&:focus-within': {
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },
    ...(state.isFocused && {
      border: 'none !important',
      boxShadow: 'none',
      outline: 'none',
    }),
  }),
  container: (provided, _state) => ({
    ...provided,
    height: '100%',
    border: 'none',
    width: '100%',
  }),
  input: (provided, _state) => ({
    ...provided,
    color: '#1a1a1a',
    outline: 'none',
    boxShadow: 'none',
    border: 'none',
    fontSize: '16px',
    width: '100%',
    height: '38px',
    '@media (max-width: 768px)': {
      fontSize: '14px',
    },
    '@media (max-width: 380px)': {
      fontSize: '13px',
    },
    padding: '0px',
    margin: '0px',
    textAlign: 'center',
    input: {
      opacity: '1 !important',
      width: '100% !important',
      textAlign: 'center',
    },
  }),
  a11yText: (provided, _state) => ({
    ...provided,
  }),
  valueContainer: (provided, _state) => ({
    ...provided,
    padding: '0',
    fontSize: '16px',
    lineHeight: '100%',
    justifyContent: 'center',
  }),
  singleValue: (provided, _state) => ({
    ...provided,
    color: '#1a1a1a',
    fontSize: '16px',
    margin: '0',
    '@media (max-width: 768px)': {
      fontSize: '14px',
    },
    '@media (max-width: 380px)': {
      fontSize: '13px',
    },
  }),
  placeholder: (provided, _state) => ({
    ...provided,
    fontSize: '16px',
    lineHeight: '100%',
    padding: '0',
    '@media (max-width: 768px)': {
      fontSize: '14px',
    },
    '@media (max-width: 380px)': {
      fontSize: '13px',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    border: 'none',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999,
    width: '100%',
    border: 'none',
    top: '40px',
    left: '0%',
    boxShadow:
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    position: 'absolute',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? colors.baseColor : 'white',
    color: state.isSelected ? 'white' : '#1a1a1a',
    border: 'none',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: colors.baseColor,
      color: 'white',
    },
    '&:hover': {
      '@media (min-width: 460px)': {
        backgroundColor: colors.lightGrey,
      },
    },
  }),
};

const Option = (props) => (
  <components.Option {...props}>
    <div className="flex-column">{props.children}</div>
  </components.Option>
);

const Menu = (props) => {
  if (!props.options || props.options.length === 0) {
    return null;
  }
  return <components.Menu {...props}>{props.children}</components.Menu>;
};

const LocationSearch = ({ defaultValue, onChange }) => {
  const [value, setValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const locationStore = useLocationStore();

  const fetchResults = useCallback(
    async (inputValue, callback) => {
      if (inputValue) {
        try {
          const locations = await locationStore.searchLocations(inputValue);
          const formattedLocations = locations.map((location) => ({
            ...location,
            value: location.id,
            label: `${location.city}, ${location.stateCode}`,
          }));
          setOptions(formattedLocations);
          callback(formattedLocations);
        } catch (err) {
          console.error(err);
          setOptions([]);
          callback([]);
        }
      } else {
        setOptions([]);
        callback([]);
      }
    },
    [locationStore],
  );

  const debouncedFetchResults = useMemo(
    () => debounce(fetchResults, 300),
    [fetchResults],
  );

  const loadOptions = useCallback(
    (inputValue, callback) => {
      debouncedFetchResults(inputValue, callback);
    },
    [debouncedFetchResults],
  );

  const handleInputChange = useCallback((newValue) => {
    setInputValue(newValue);
    return newValue;
  }, []);

  const handleChange = useCallback(
    (selectedValue) => {
      setValue(selectedValue);
      setInputValue('');
      setMenuIsOpen(false);
      onChange?.(selectedValue);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setValue(null);
    setInputValue('');
    setMenuIsOpen(false);
    onChange?.(null);
  }, [onChange]);

  return (
    <div className="search-container relative !block !h-[inherit]">
      <AsyncSelect
        value={value}
        placeholder="Search Locations"
        loadOptions={loadOptions}
        defaultOptions={false}
        inputValue={inputValue}
        onMenuClose={() => setMenuIsOpen(false)}
        onMenuOpen={() => setMenuIsOpen(true)}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: colors.baseColor,
            primary50: colors.lightGrey,
            primary25: colors.white,
            danger: colors.baseColor,
            dangerLight: colors.baseColorHalfTransparent,
          },
        })}
        styles={styles}
        onInputChange={handleInputChange}
        components={{
          ...animatedComponents,
          Option,
          Menu,
          ClearIndicator: () => null,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        onChange={handleChange}
        autoLoad={false}
      />
      {Boolean(value) && (
        <button
          className="absolute top-2 right-1 bg-transparent border-none cursor-pointer"
          onClick={handleClear}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default LocationSearch;
